import type { AnyExtension } from '@tiptap/core';
import { ReactNodeViewRenderer } from '@tiptap/react';
import Collaboration from '@tiptap/extension-collaboration';
import CodeBlockLowlight from '@tiptap/extension-code-block-lowlight';
import { Placeholder } from '@tiptap/extensions';
import { common, createLowlight } from 'lowlight';
import type * as Y from 'yjs';
import Image from '@tiptap/extension-image';
import { Callout, Embed, Hashtag, RawBlock, schemaExtensions, WikiLink } from '../schema';
import { createFileDrop } from './files';
import type { EditorHost } from './host';
import { TaskBehaviour } from './taskBehaviour';
import { ListIndent } from './listIndent';
import {
  CalloutView,
  CodeBlockView,
  EmbedView,
  HashtagView,
  ImageView,
  RawBlockView,
  WikiLinkView,
} from './nodeviews';
import {
  createSlashCommands,
  createTagSuggest,
  createTypingRules,
  createWikiLinkSuggest,
} from './suggestions';

const lowlight = createLowlight(common);

export interface EditorExtensionOptions {
  document: Y.Doc;
  host: EditorHost;
  placeholder: string;
}

/** The full editor: schema + node views + collaboration + typing aids. */
export function createEditorExtensions(opts: EditorExtensionOptions): AnyExtension[] {
  const withViews: Record<string, AnyExtension> = {
    wikiLink: WikiLink.extend({ addNodeView: () => ReactNodeViewRenderer(WikiLinkView) }),
    hashtag: Hashtag.extend({ addNodeView: () => ReactNodeViewRenderer(HashtagView) }),
    embed: Embed.extend({ addNodeView: () => ReactNodeViewRenderer(EmbedView) }),
    callout: Callout.extend({ addNodeView: () => ReactNodeViewRenderer(CalloutView) }),
    rawBlock: RawBlock.extend({ addNodeView: () => ReactNodeViewRenderer(RawBlockView) }),
    image: Image.extend({ addNodeView: () => ReactNodeViewRenderer(ImageView) }).configure({
      inline: true,
    }),
  };
  const codeBlock = CodeBlockLowlight.extend({
    addNodeView: () => ReactNodeViewRenderer(CodeBlockView),
  }).configure({ lowlight, defaultLanguage: null });

  return [
    ...schemaExtensions({ codeBlock }).map((ext) => withViews[ext.name] ?? ext),
    Collaboration.configure({ document: opts.document, field: 'default' }),
    Placeholder.configure({ placeholder: opts.placeholder, includeChildren: false }),
    TaskBehaviour,
    ListIndent,
    createSlashCommands(opts.host),
    createWikiLinkSuggest(opts.host),
    createTagSuggest(opts.host),
    createTypingRules(opts.host),
    createFileDrop(opts.host),
  ];
}
